import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Truck, Users, Receipt, AlertTriangle, TrendingUp, Clock, CheckCircle, Download, MapPin, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const res = await axios.get('/admin/analytics');
      setAnalytics(res.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get('/export/logbook/pdf', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fahrtenbuch_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Fahrtenbuch wurde heruntergeladen');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Fehler beim Exportieren des Fahrtenbuchs');
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-white mb-2">
          Admin Dashboard
        </h1>
        <p className="text-zinc-400">Zentrale Verwaltung & Übersicht</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-red-900/30 to-red-800/20 border-red-700 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/admin/geofencing')}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-red-600/30 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-red-400" />
              </div>
              <div>
                <h3 className="font-bold text-white">Geofencing</h3>
                <p className="text-sm text-zinc-400">Grenzen verwalten</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border-blue-700 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={handleExportPdf}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-blue-600/30 flex items-center justify-center">
                {exportingPdf ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-400"></div>
                ) : (
                  <Download className="h-6 w-6 text-blue-400" />
                )}
              </div>
              <div>
                <h3 className="font-bold text-white">Fahrtenbuch</h3>
                <p className="text-sm text-zinc-400">Als PDF exportieren</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-900/30 to-green-800/20 border-green-700 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/admin/tracking')}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-green-600/30 flex items-center justify-center">
                <Truck className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <h3 className="font-bold text-white">Live Tracking</h3>
                <p className="text-sm text-zinc-400">Fahrzeuge verfolgen</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="vehicles-kpi">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Fahrzeuge Gesamt</CardTitle>
            <Truck className="h-4 w-4 text-zinc-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{analytics?.vehicles?.total || 0}</div>
            <div className="flex gap-4 mt-3 text-xs">
              <div className="text-green-400">
                <CheckCircle className="h-3 w-3 inline mr-1" />
                {analytics?.vehicles?.available || 0} verfügbar
              </div>
              <div className="text-blue-400">
                <TrendingUp className="h-3 w-3 inline mr-1" />
                {analytics?.vehicles?.active || 0} aktiv
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="users-kpi">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Mitarbeiter</CardTitle>
            <Users className="h-4 w-4 text-zinc-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{analytics?.users?.total || 0}</div>
            <div className="flex gap-4 mt-3 text-xs">
              <div className="text-zinc-400">
                {analytics?.users?.drivers || 0} Fahrer
              </div>
              <div className="text-zinc-400">
                {analytics?.users?.admins || 0} Admins
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="receipts-kpi">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Belege</CardTitle>
            <Receipt className="h-4 w-4 text-zinc-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{analytics?.receipts?.total || 0}</div>
            <div className="flex gap-4 mt-3 text-xs">
              <div className="text-amber-400">
                <Clock className="h-3 w-3 inline mr-1" />
                {analytics?.receipts?.pending_sync || 0} ausstehend
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="costs-kpi">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Gesamtkosten</CardTitle>
            <TrendingUp className="h-4 w-4 text-zinc-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">
              €{(analytics?.receipts?.total_costs || 0).toFixed(2)}
            </div>
            <div className="text-xs text-zinc-400 mt-3">
              Alle Belege
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activities */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-white">Letzte Aktivitäten</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analytics?.recent_activities?.length > 0 ? (
              analytics.recent_activities.map((activity, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-4 rounded-lg bg-zinc-800 border border-zinc-700"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-zinc-700 flex items-center justify-center">
                      {activity.action === 'checkin' ? (
                        <CheckCircle className="h-5 w-5 text-green-400" />
                      ) : (
                        <Clock className="h-5 w-5 text-blue-400" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-white capitalize">
                        Fahrzeug {activity.action === 'checkin' ? 'übernommen' : 'zurückgegeben'}
                      </div>
                      <div className="text-sm text-zinc-400">
                        {activity.vehicle_id} - {activity.km_reading?.toLocaleString()} km
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-zinc-500">
                    {new Date(activity.created_at).toLocaleString('de-DE')}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-zinc-500">
                Keine aktuellen Aktivitäten
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Maintenance Alerts */}
      {analytics?.vehicles?.maintenance > 0 && (
        <Card className="bg-amber-900/20 border-amber-800">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              <CardTitle className="text-xl font-bold text-amber-100">
                Wartung erforderlich
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-amber-200">
              {analytics.vehicles.maintenance} Fahrzeug(e) befinden sich derzeit in Wartung.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminDashboard;
